import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { CheckCircle2, XCircle, AlertTriangle, Calendar, Clock, Users } from 'lucide-react';
import { motion } from 'framer-motion';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CancelReservationPage() {
  const { id } = useParams();
  const { t } = useLanguage();
  const [reservation, setReservation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);
  const [cancelled, setCancelled] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    axios.get(`${API}/reservations/${id}`)
      .then(r => { setReservation(r.data); if (r.data.status === 'cancelled') setCancelled(true); })
      .catch(() => setError(t('reservation_not_found_text')))
      .finally(() => setLoading(false));
  }, [id, t]);

  const handleCancel = async () => {
    setCancelling(true);
    try { await axios.post(`${API}/reservations/${id}/cancel`); setCancelled(true); }
    catch { setError(t('error_generic')); }
    finally { setCancelling(false); }
  };

  if (loading) return <div className="min-h-screen bg-[hsl(38,44%,96%)] flex items-center justify-center"><div className="animate-pulse text-[hsl(22,10%,38%)]">{t('loading')}</div></div>;

  if (error && !reservation) return (
    <div className="min-h-screen bg-[hsl(38,44%,96%)] flex items-center justify-center px-4">
      <div className="text-center max-w-md">
        <XCircle className="w-16 h-16 mx-auto mb-4 text-[hsl(0,72%,45%)]" />
        <h2 className="font-[family-name:var(--font-display)] text-2xl mb-3">{t('reservation_not_found')}</h2>
        <p className="text-[hsl(22,10%,38%)] mb-6">{t('reservation_not_found_text')}</p>
        <Link to="/" className="inline-flex items-center px-6 py-3 rounded-xl text-sm font-semibold bg-[hsl(16,55%,36%)] text-white hover:brightness-[1.05] transition-colors duration-200">{t('to_homepage')}</Link>
      </div>
    </div>
  );

  if (cancelled) return (
    <div className="min-h-screen bg-[hsl(38,44%,96%)] flex items-center justify-center px-4">
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="text-center max-w-md">
        <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-[hsl(0,72%,45%)]/10 flex items-center justify-center"><CheckCircle2 className="w-10 h-10 text-[hsl(0,72%,45%)]" /></div>
        <h2 className="font-[family-name:var(--font-display)] text-2xl mb-3">{t('cancel_success')}</h2>
        <p className="text-[hsl(22,10%,38%)] mb-2">{t('cancel_success_text')}</p>
        <p className="text-sm text-[hsl(22,10%,38%)] mb-8">{t('cancel_success_subtitle')}</p>
        <div className="flex flex-col sm:flex-row justify-center gap-3">
          <Link to="/reservation" className="inline-flex items-center justify-center px-6 py-3 rounded-xl text-sm font-semibold bg-[hsl(16,55%,36%)] text-white hover:brightness-[1.05] transition-colors duration-200">{t('rebook')}</Link>
          <Link to="/" className="inline-flex items-center justify-center px-6 py-3 rounded-xl text-sm font-semibold border border-[hsl(32,17%,84%)] text-[hsl(20,17%,12%)] hover:bg-[hsl(35,20%,92%)] transition-colors duration-200">{t('to_homepage')}</Link>
        </div>
      </motion.div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[hsl(38,44%,96%)]">
      <section className="bg-[hsl(20,17%,12%)] text-white py-16 sm:py-20">
        <div className="mx-auto max-w-[72rem] px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="font-[family-name:var(--font-display)] text-3xl sm:text-4xl tracking-[var(--tracking-display)] mb-3">{t('cancel_reservation')}</h1>
          <p className="text-white/70">{t('cancel_question')}</p>
        </div>
      </section>
      <div className="mx-auto max-w-lg px-4 sm:px-6 lg:px-8 py-12">
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-2xl border border-[hsl(32,17%,84%)] shadow-[var(--shadow-sm)] overflow-hidden">
          <div className="bg-[hsl(42,72%,44%)]/10 px-6 py-4 flex items-center gap-3 border-b border-[hsl(32,17%,84%)]">
            <AlertTriangle className="w-5 h-5 text-[hsl(42,72%,44%)] flex-shrink-0" />
            <p className="text-sm text-[hsl(20,17%,12%)]">{t('cancel_warning')}</p>
          </div>
          <div className="p-6">
            <h3 className="font-[family-name:var(--font-display)] text-lg mb-4">{t('your_reservation')}</h3>
            <div className="bg-[hsl(36,33%,92%)] rounded-xl p-5 space-y-3">
              <div className="flex items-center gap-3"><div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center"><Calendar className="w-5 h-5 text-[hsl(16,55%,36%)]" /></div><div><p className="text-xs text-[hsl(22,10%,38%)]">{t('date')}</p><p className="font-semibold text-sm">{reservation?.date}</p></div></div>
              <div className="flex items-center gap-3"><div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center"><Clock className="w-5 h-5 text-[hsl(16,55%,36%)]" /></div><div><p className="text-xs text-[hsl(22,10%,38%)]">{t('time')}</p><p className="font-semibold text-sm">{reservation?.time}</p></div></div>
              <div className="flex items-center gap-3"><div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center"><Users className="w-5 h-5 text-[hsl(16,55%,36%)]" /></div><div><p className="text-xs text-[hsl(22,10%,38%)]">{t('party_size')}</p><p className="font-semibold text-sm">{reservation?.party_size} {reservation?.party_size === 1 ? t('person') : t('persons')}</p></div></div>
            </div>
            {error && <div className="mt-4 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>}
            <div className="mt-6 space-y-3">
              <button onClick={handleCancel} disabled={cancelling} data-testid="cancel-reservation-button" className="w-full py-3.5 rounded-xl text-sm font-semibold bg-[hsl(0,72%,45%)] text-white hover:brightness-[1.05] transition-colors duration-200 disabled:opacity-50">
                {cancelling ? t('cancelling') : t('cancel_confirm')}
              </button>
              <Link to="/" className="block w-full py-3.5 rounded-xl text-sm font-semibold border border-[hsl(32,17%,84%)] text-[hsl(20,17%,12%)] text-center hover:bg-[hsl(35,20%,92%)] transition-colors duration-200">{t('cancel_deny')}</Link>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
